import '../App.css';
import React from 'react';
import ReactGA from 'react-ga4';
import { Link } from 'react-router-dom';
import { Button, Icon, Label, Message, Popup, Table } from 'semantic-ui-react';
import { addMonths, format, parse, startOfMonth } from 'date-fns';
import withRouter from '../utils/withRouter';
import Page from '../components/Page';
import api from '../utils/api';
import colors from '../utils/colors';
import memberCache from '../caches/memberCache';
import servicesCache from '../caches/servicesCache';
import ServicesPreviewPopup from '../popups/ServicesPreviewPopup';
import ServicesSettingsPopup from '../popups/ServicesSettingsPopup';
import ServicesNotifyPopup from '../popups/ServicesNotifyPopup';
import ServiceCellEditor from '../components/ServiceCellEditor';
import ServicesFilterDropdown from '../components/ServicesFilterDropdown';
import ServicesHistoryPanel from '../components/ServicesHistoryPanel';
import { shortNamesDict } from '../utils/shortName';

const MONTH_PARAM = 'yyyy-MM';
const FILTERS_KEY = 'services.filters';
const WIDTHS_KEY = 'services.columnWidths';
const DEFAULT_WIDTHS = { date: 110, service: 100 };
const DEFAULT_ROLE_WIDTH = 190;
const MIN_WIDTH = 60;
const SKELETON_ROWS = 9;
const norm = (s) => (s ?? '').trim();
const sameSet = (a, b) => a.length === b.length && a.every(x => b.includes(x));

/** per-viewer conveniences only: which services / roles are shown, column widths */
const readStored = (key) => {
	try {
		const value = JSON.parse(window.localStorage.getItem(key));
		return value && typeof value === 'object' ? value : {};
	}
	catch { return {}; }
};
const writeStored = (key, value) => {
	try { window.localStorage.setItem(key, JSON.stringify(value)); }
	catch { /* private mode / storage blocked */ }
};

/** Faint row tint by start time, so all the 9 AM / 11 AM / evening rows are easy to follow down the month */
const serviceTint = (start) => {
	const d = new Date(start);
	const hour = d.getHours() + d.getMinutes() / 60;
	if (hour < 10.5) return 'svc-tint-morning';
	if (hour < 15) return 'svc-tint-midday';
	return 'svc-tint-evening';
};

const serviceTitle = (service, row) => norm(service?.alias) || format(new Date(row.start), 'h:mm a');

function MemberLine({ member, name }) {
	const responded = member.confirmed !== undefined && member.confirmed !== null;
	const icon = responded ? (member.confirmed ? 'check circle' : 'times circle') : (member.notified ? 'wait' : 'circle outline');
	const color = responded ? (member.confirmed ? colors.accept : colors.decline) : (member.notified ? colors.pending : colors.midGray);
	const title = responded ? (member.confirmed ? 'Confirmed' : 'Declined') : (member.notified ? 'Notified, no answer yet' : 'Not notified yet');

	return (
		<div style={{ display: 'flex', alignItems: 'center', lineHeight: 1.5 }} title={title}>
			<Icon name={icon} style={{ color, marginRight: 4 }} />
			<span style={{ textDecoration: member.confirmed === false ? 'line-through' : 'none' }}>{name ?? 'Unknown'}</span>
		</div>
	);
}

function SkeletonBar({ width }) {
	return <div className='svc-skeleton' style={{ width, height: 14, margin: '4px 0' }} />;
}

class ServicesPage extends React.Component {
	constructor(props) {
		super(props);

		const param = new URLSearchParams(window.location.search).get('month');
		const parsed = param ? parse(param, MONTH_PARAM, new Date()) : new Date();
		const month = startOfMonth(isNaN(parsed) ? new Date() : parsed);
		const filters = readStored(FILTERS_KEY);

		this.state = {
			month,
			// cached config lets the table header render before the data arrives
			config: servicesCache.config,
			rows: [],
			/** rowKey -> roleId -> { memberIds?, notes? } (only what was edited) */
			edits: {},
			editing: null,
			loading: true,
			error: null,
			success: null,
			// bumped after a save so the change history reloads (history is written right after the save lands)
			historyKey: 0,
			memberOptions: [],
			memberNamesDict: {},
			/** id -> "E. Yakovlev" */
			shortNames: {},
			/** column key ('date', 'service', role id) -> px; missing = default */
			colWidths: readStored(WIDTHS_KEY),
			preview: null,
			previewLoading: false,
			saving: false,
			showSettings: false,
			setup: null,
			notify: null,
			notifyLoading: false,
			notifySending: false,
			/** null = all */
			serviceFilter: Array.isArray(filters.services) ? filters.services : null,
			roleFilter: Array.isArray(filters.roles) ? filters.roles : null,
		};
	}

	/** Everyone signed in can view; only staff and admins edit, save and text */
	canEdit = () => !!(this.props.auth?.staff || this.props.auth?.admin);

	componentDidMount = async () => {
		ReactGA.send('pageview');
		window.addEventListener('beforeunload', this.onBeforeUnload);

		// people and the month load in parallel
		const people = Promise.all([memberCache.GetMemberOptionsArray(), memberCache.GetMemberNamesDict()])
			.then(([options, memberNamesDict]) => {
				const shortNames = shortNamesDict(memberNamesDict);
				// short names, no profile pictures; search still matches the full name
				const memberOptions = options
					.map(o => ({ key: o.key, value: o.value, text: shortNames[o.value] ?? o.text }))
					.sort((a, b) => a.text.localeCompare(b.text));
				this.setState({ memberOptions, memberNamesDict, shortNames });
			});
		await Promise.all([people, this.load()]);
		this.prefetchSetup();
	}

	componentWillUnmount = () => {
		window.removeEventListener('beforeunload', this.onBeforeUnload);
		this.stopResize?.();
	}

	onBeforeUnload = (e) => {
		if (this.editCount() === 0) return;
		e.preventDefault();
		e.returnValue = '';
	}

	/** Settings data loads in the background so the popup opens instantly */
	prefetchSetup = () => {
		if (!this.props.auth?.admin) return;
		api.getServicesSetup().then(setup => this.setState({ setup })).catch(err => console.warn('Could not prefetch services setup', err));
	}

	editCount = () => Object.values(this.state.edits).reduce((n, roles) => n + Object.keys(roles).length, 0);

	/** keepMessages: keep the save / settings result visible while reloading */
	load = async ({ keepMessages = false } = {}) => {
		const { month } = this.state;
		this.setState(prev => ({ loading: true, editing: null, error: keepMessages ? prev.error : null, success: keepMessages ? prev.success : null }));
		try {
			const data = await api.getServicesMatrix(format(month, MONTH_PARAM));
			servicesCache.Set(data.config);

			// edits for rows that no longer exist (event deleted / created by someone else) can't be saved: drop them
			const keys = new Set(data.rows.map(r => r.key));
			const orphaned = Object.keys(this.state.edits).filter(k => !keys.has(k));
			const edits = Object.fromEntries(Object.entries(this.state.edits).filter(([k]) => keys.has(k)));

			this.setState({
				config: data.config,
				rows: data.rows,
				edits,
				loading: false,
				error: [this.state.error, orphaned.length > 0 ? `${orphaned.length} edited row(s) changed on the server and were reloaded. Please re-check them.` : null].filter(Boolean).join(' · ') || null,
			});
		}
		catch (error) {
			this.setState({ error: `Could not load services: ${error}`, loading: false });
		}
	}

	changeMonth = (delta) => {
		if (this.editCount() > 0 && !window.confirm('You have unsaved changes. Discard them?')) return;

		const month = delta === 0 ? startOfMonth(new Date()) : addMonths(this.state.month, delta);
		const url = new URL(window.location.href);
		url.searchParams.set('month', format(month, MONTH_PARAM));
		window.history.replaceState(null, '', url.toString());

		this.setState({ month, edits: {}, success: null }, this.load);
	}

	setFilter = (key, values, allValues) => {
		// all selected (or none) = show everything, and keep showing new services/roles added later
		const value = values.length === 0 || sameSet(values, allValues) ? null : values;
		this.setState({ [key]: value }, () => writeStored(FILTERS_KEY, { services: this.state.serviceFilter, roles: this.state.roleFilter }));
	}

	colWidth = (key) => this.state.colWidths[key] ?? DEFAULT_WIDTHS[key] ?? DEFAULT_ROLE_WIDTH;

	/** drag a header's right edge to resize that column; double-click resets it */
	startResize = (key, e) => {
		if (e.button !== undefined && e.button !== 0) return;
		e.preventDefault();
		e.stopPropagation();
		this.stopResize?.();

		const startX = e.clientX;
		const startWidth = this.colWidth(key);
		const onMove = (ev) => {
			const width = Math.max(MIN_WIDTH, Math.round(startWidth + ev.clientX - startX));
			this.setState(prev => prev.colWidths[key] === width ? null : { colWidths: { ...prev.colWidths, [key]: width } });
		};
		const stop = () => {
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', stop);
			window.removeEventListener('pointercancel', stop);
			document.body.classList.remove('svc-resizing');
			this.stopResize = null;
			writeStored(WIDTHS_KEY, this.state.colWidths);
		};
		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerup', stop);
		window.addEventListener('pointercancel', stop);
		document.body.classList.add('svc-resizing');
		this.stopResize = stop;
	}

	resetWidth = (key) => this.setState(prev => {
		const colWidths = { ...prev.colWidths };
		delete colWidths[key];
		return { colWidths };
	}, () => writeStored(WIDTHS_KEY, this.state.colWidths))

	renderHeaderCell = (key, content) =>
		<Table.HeaderCell key={key} className='svc-th' style={{ width: this.colWidth(key) }}>
			<div className='svc-th-label'>{content}</div>
			<div
				className='svc-col-resizer'
				title='Drag to resize · double-click to reset'
				onPointerDown={(e) => this.startResize(key, e)}
				onDoubleClick={() => this.resetWidth(key)}
			/>
		</Table.HeaderCell>

	/** people search in the cell editor: matches the short name or the full name ("yak", "eugene", "e. yak") */
	searchMembers = (options, query) => {
		const q = norm(query).toLocaleLowerCase();
		if (!q) return options;
		const { memberNamesDict } = this.state;
		return options.filter(o => o.text.toLocaleLowerCase().includes(q) || (memberNamesDict[o.value] ?? '').toLocaleLowerCase().includes(q));
	}

	/** Current value of a cell: the edit if there is one, otherwise what the event has */
	cellValue = (row, role) => {
		const cell = row.cells[role.id] ?? { memberIds: [], notes: '', members: [] };
		const edit = this.state.edits[row.key]?.[role.id];
		return {
			memberIds: edit?.memberIds ?? cell.memberIds,
			notes: edit?.notes ?? cell.notes,
			notesEdited: edit?.notes !== undefined,
			membersEdited: edit?.memberIds !== undefined,
			edited: !!edit,
		};
	}

	setEdit = (row, role, patch) => {
		this.setState(prev => {
			const original = row.cells[role.id];
			const edits = { ...prev.edits, [row.key]: { ...(prev.edits[row.key] ?? {}) } };
			const next = { ...(edits[row.key][role.id] ?? {}), ...patch };

			// drop the edit when it is back to the original value (and the cell was not mixed)
			if (next.memberIds === undefined || (sameSet(next.memberIds, original.memberIds) && !original.mixed)) delete next.memberIds;
			if (next.notes === undefined || (norm(next.notes) === norm(original.notes) && !original.notesMixed)) delete next.notes;

			if (Object.keys(next).length === 0) delete edits[row.key][role.id];
			else edits[row.key][role.id] = next;
			if (Object.keys(edits[row.key]).length === 0) delete edits[row.key];

			return { edits, success: null };
		});
	}

	buildChanges = () => {
		const { rows, edits } = this.state;
		return Object.entries(edits).flatMap(([rowKey, roleEdits]) => {
			const row = rows.find(r => r.key === rowKey);
			if (!row) return [];
			const cells = {};
			for (const [roleId, edit] of Object.entries(roleEdits)) {
				const cell = {};
				if (edit.memberIds !== undefined) cell.memberIds = edit.memberIds;
				if (edit.notes !== undefined) cell.notes = edit.notes;
				cells[roleId] = cell;
			}
			return [{ templateId: row.templateId, day: row.day, eventId: row.eventId, version: row.version, cells }];
		});
	}

	openPreview = async () => {
		this.setState({ previewLoading: true, error: null });
		try {
			const preview = await api.saveServices(this.buildChanges(), true, true);
			this.setState({ preview, previewLoading: false });
		}
		catch (error) {
			this.setState({ error: `Could not preview changes: ${error}`, previewLoading: false });
		}
	}

	confirmSave = async (notify) => {
		this.setState({ saving: true });
		try {
			const result = await api.saveServices(this.buildChanges(), notify, false);
			const failed = result.results.filter(r => r.status === 'error' || r.status === 'conflict');
			const done = result.results.filter(r => r.status === 'updated' || r.status === 'created');
			const texted = done.reduce((n, r) => n + r.texted.length, 0);

			// keep edits that failed validation so they can be fixed; drop conflicted ones so a reload
			// never quietly overwrites someone else's change with a newer version
			const keepKeys = new Set(failed.filter(r => r.status === 'error').map(r => r.key));
			const edits = Object.fromEntries(Object.entries(this.state.edits).filter(([key]) => keepKeys.has(this.changeKey(key))));
			const conflicts = failed.filter(r => r.status === 'conflict');

			this.setState({
				saving: false,
				preview: null,
				edits,
				success: `Saved ${done.length} service${done.length === 1 ? '' : 's'}${notify && texted > 0 ? `, texted ${texted} ${texted === 1 ? 'person' : 'people'}` : ''}.`,
				error: failed.length > 0
					? failed.map(r => `${r.label} ${r.day}: ${r.message}`).join(' · ') + (conflicts.length > 0 ? ' (Your edits for those services were not saved; they now show the latest values.)' : '')
					: null,
			}, () => {
				this.load({ keepMessages: true });
				setTimeout(() => this.setState(prev => ({ historyKey: prev.historyKey + 1 })), 2500);
			});
		}
		catch (error) {
			this.setState({ saving: false, error: `Save failed: ${error}` });
		}
	}

	/** Matrix row key -> save result key (same format the server uses) */
	changeKey = (rowKey) => {
		const row = this.state.rows.find(r => r.key === rowKey);
		return row?.eventId ? `${row.templateId}_${row.day}_${row.eventId}` : `${row?.templateId}_${row?.day}`;
	}

	/** null = all; drops ids no longer in the config */
	effectiveFilter = (filter, allIds) => {
		if (!filter || allIds.length === 0) return null;
		const kept = filter.filter(id => allIds.includes(id));
		return kept.length === 0 ? null : kept;
	}

	notifyFilters = () => {
		const { config, month, serviceFilter, roleFilter } = this.state;
		return {
			month: format(month, MONTH_PARAM),
			templateIds: this.effectiveFilter(serviceFilter, (config?.services ?? []).map(s => s.templateId)) ?? undefined,
			roleIds: this.effectiveFilter(roleFilter, (config?.roles ?? []).map(r => r.id)) ?? undefined,
		};
	}

	openNotify = async () => {
		this.setState({ notifyLoading: true, error: null });
		try {
			const notify = await api.notifyServices(this.notifyFilters(), true);
			this.setState({ notify, notifyLoading: false });
		}
		catch (error) {
			this.setState({ error: `Could not check confirmations: ${error}`, notifyLoading: false });
		}
	}

	confirmNotify = async () => {
		this.setState({ notifySending: true });
		try {
			// send only to exactly who was previewed
			const only = Object.fromEntries((this.state.notify?.results ?? []).map(r => [r.eventId, [...r.texted, ...(r.blockouts ?? [])]]));
			const result = await api.notifyServices({ ...this.notifyFilters(), only }, false);
			const texted = result.results.reduce((n, r) => n + (r.status === 'sent' ? r.texted.length : 0), 0);
			const failed = result.results.filter(r => r.status === 'error');
			this.setState({
				notifySending: false,
				notify: null,
				success: `Sent ${texted} confirmation text${texted === 1 ? '' : 's'}.`,
				error: failed.length > 0 ? failed.map(r => `${r.label} ${r.day}: ${r.message}`).join(' · ') : null,
			}, () => this.load({ keepMessages: true }));
		}
		catch (error) {
			this.setState({ notifySending: false, error: `Sending texts failed: ${error}` });
		}
	}

	/** part: 'people' (click on the names / "+ Assign Person") or 'notes' (click on the text / "+ Assign Text") */
	openCell = (row, role, part = 'people') => {
		if (row.past || !this.canEdit()) return;
		const { editing } = this.state;
		if (editing?.rowKey === row.key && editing?.roleId === role.id && editing?.part === part) return;
		this.setState({ editing: { rowKey: row.key, roleId: role.id, part } });
	}

	closeCell = () => this.setState({ editing: null });

	renderCell = (row, role) => {
		const { shortNames, editing, memberOptions } = this.state;
		const cell = row.cells[role.id];
		if (!cell || cell.fieldCount === 0) return <Table.Cell key={role.id} disabled textAlign='center' style={{ color: colors.midGray }}>—</Table.Cell>;

		const value = this.cellValue(row, role);
		const editable = !row.past && this.canEdit();
		const isEditing = editable && editing?.rowKey === row.key && editing?.roleId === role.id;
		const editingPart = isEditing ? (editing.part === 'notes' && role.notes ? 'notes' : 'people') : null;
		const statusById = Object.fromEntries((cell.members ?? []).map(m => [m.id, m]));
		// only roles with notes (Scripture Reading, Preaching) show / edit notes; Worship's are per-field (songs) and left alone
		const hasNotes = !!role.notes;
		const showNotes = hasNotes && (value.notesEdited ? norm(value.notes) : (!cell.notesMixed && norm(cell.notes)));
		const mixedNotes = hasNotes && !value.notesEdited && cell.notesMixed && cell.notesValues?.length > 0;
		const nameOf = (id) => shortNames[id] ?? 'Unknown';
		const part = (which) => (e) => { e.stopPropagation(); this.openCell(row, role, which); };
		const editor = (mode) =>
			<ServiceCellEditor
				key={`${row.key}_${role.id}_${mode}`}
				mode={mode}
				cell={cell}
				value={value}
				currentText={value.memberIds.map(nameOf).join(', ')}
				options={memberOptions}
				search={this.searchMembers}
				notesPlaceholder={role.id === 'preaching' ? 'Topic / passage' : 'Passage / notes'}
				onChange={(patch) => this.setEdit(row, role, patch)}
				onClose={this.closeCell}
			/>;

		return (
			<Table.Cell
				key={role.id}
				verticalAlign='top'
				warning={value.edited}
				selectable={editable && !isEditing}
				className={[value.edited && 'svc-edited', isEditing && 'svc-cell-editing'].filter(Boolean).join(' ') || undefined}
				style={{ padding: 8, cursor: !editable || isEditing ? 'default' : 'pointer', overflowWrap: 'anywhere' }}
				onClick={() => this.openCell(row, role)}
			>
				{cell.mixed && !value.edited && editingPart !== 'people' &&
					<Popup
						content={`The ${cell.fieldCount} linked fields on this event have different people. Saving this cell makes them all match.`}
						trigger={<Label size='mini' color='orange' style={{ marginBottom: 4 }}>Mixed</Label>}
					/>
				}
				{editingPart === 'people'
					? editor('people')
					: <div className='svc-cell-part' onClick={part('people')}>
						{value.memberIds.length === 0 && editable && <div className='svc-assign'>+ Assign Person</div>}
						{value.memberIds.map(id =>
							value.edited && value.membersEdited
								? <div key={id} style={{ lineHeight: 1.5 }}><Icon name='pencil' style={{ color: colors.midGray, marginRight: 4 }} />{nameOf(id)}</div>
								: <MemberLine key={id} member={statusById[id] ?? { id }} name={nameOf(id)} />
						)}
					</div>
				}
				{hasNotes && (editingPart === 'notes'
					? <div style={{ marginTop: 4 }}>{editor('notes')}</div>
					: <div className='svc-cell-part' onClick={part('notes')} style={{ marginTop: 4 }}>
						{showNotes && <div style={{ fontStyle: 'italic', color: colors.accent, whiteSpace: 'pre-wrap' }}>{value.notes}</div>}
						{mixedNotes && <div style={{ fontStyle: 'italic', color: colors.midGray }} title='The linked fields have different notes'>{cell.notesValues.join(' / ')}</div>}
						{!showNotes && !mixedNotes && editable && <div className='svc-assign'>+ Assign Text</div>}
					</div>
				)}
			</Table.Cell>
		);
	}

	renderSkeletonRows = (columns) => [...Array(SKELETON_ROWS)].map((_, idx) =>
		<Table.Row key={`skeleton_${idx}`}>
			<Table.Cell>{idx % 3 === 0 && <SkeletonBar width='70%' />}</Table.Cell>
			<Table.Cell><SkeletonBar width='60%' /></Table.Cell>
			{[...Array(columns)].map((__, c) => <Table.Cell key={c}><SkeletonBar width={`${55 + ((idx + c) % 3) * 15}%`} /></Table.Cell>)}
		</Table.Row>
	);

	render() {
		const {
			month, config, rows, loading, error, success, preview, previewLoading, saving, showSettings, setup,
			memberNamesDict, serviceFilter, roleFilter, notify, notifyLoading, notifySending,
		} = this.state;
		const isAdmin = !!this.props.auth?.admin;
		const canEdit = this.canEdit();
		const monthLabel = format(month, 'MMMM yyyy');

		const allServices = [...(config?.services ?? [])].sort((a, b) => a.order - b.order);
		const allRoles = [...(config?.roles ?? [])].sort((a, b) => a.order - b.order);
		// saved filters may name services/roles that were removed since: fall back to all
		const activeServices = this.effectiveFilter(serviceFilter, allServices.map(s => s.templateId));
		const activeRoles = this.effectiveFilter(roleFilter, allRoles.map(r => r.id));
		const roles = allRoles.filter(r => !activeRoles || activeRoles.includes(r.id));
		const visibleRows = rows.filter(r => !activeServices || activeServices.includes(r.templateId));
		const editCount = this.editCount();
		// edit mode: a cell is open or there are unsaved changes. Only Save / Discard stay; filters, Send SMS and
		// Settings hide, and month navigation is shown but disabled until the changes are saved or discarded.
		const editMode = canEdit && (editCount > 0 || !!this.state.editing);
		const notConfigured = !loading && config && (config.services.length === 0 || allRoles.length === 0);
		const hasNotifyRoles = allRoles.some(r => r.notify);
		// fixed layout: each column keeps the width it was given (editing a cell never widens it)
		const tableWidth = this.colWidth('date') + this.colWidth('service') + (config ? roles : [0, 1, 2]).reduce((w, r) => w + (r.id ? this.colWidth(r.id) : DEFAULT_ROLE_WIDTH), 0);

		// rows grouped by calendar day for the date column
		const days = [];
		visibleRows.forEach(row => {
			const last = days[days.length - 1];
			if (last?.day === row.day) last.rows.push(row);
			else days.push({ day: row.day, rows: [row] });
		});

		return (
			<Page
				header='Services'
				helmet='Services - Scheduly'
				error={error}
				success={success}
				windowWidth={this.props.windowWidth}
			>
				<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 15 }}>
					<Button.Group>
						<Button icon='chevron left' aria-label='Previous month' onClick={() => this.changeMonth(-1)} disabled={loading || editMode} />
						<Button onClick={() => this.changeMonth(0)} disabled={loading || editMode}>Today</Button>
						<Button icon='chevron right' aria-label='Next month' onClick={() => this.changeMonth(1)} disabled={loading || editMode} />
					</Button.Group>
					<div style={{ fontSize: 22, fontWeight: 'bold', marginLeft: 10, marginRight: canEdit ? 'auto' : 0 }}>{monthLabel}</div>
					{!canEdit && <Label basic size='small' style={{ marginRight: 'auto' }} title='Only staff can change services'><Icon name='eye' />View only</Label>}

					{!editMode && allServices.length > 0 && allRoles.length > 0 &&
						<ServicesFilterDropdown
							services={allServices.map(s => ({ id: s.templateId, name: s.label }))}
							roles={allRoles.map(r => ({ id: r.id, name: r.name }))}
							activeServices={activeServices}
							activeRoles={activeRoles}
							onChange={(kind, ids) => kind === 'services'
								? this.setFilter('serviceFilter', ids, allServices.map(s => s.templateId))
								: this.setFilter('roleFilter', ids, allRoles.map(r => r.id))}
						/>
					}
					{canEdit && hasNotifyRoles && !editMode &&
						<Popup
							content={`Send SMS: text everyone in ${monthLabel} services (shown filters) who hasn't been asked to confirm yet. Other months are not included. You'll see the list before anything is sent.`}
							trigger={<Button basic icon='paper plane outline' aria-label='Send SMS' loading={notifyLoading} disabled={loading || notifyLoading} onClick={this.openNotify} />}
						/>
					}
					{isAdmin && !editMode && <Button basic icon='setting' title='Settings' aria-label='Settings' onClick={() => this.setState({ showSettings: true })} />}
					{canEdit && editCount > 0 && <Button basic onClick={() => window.confirm('Discard all unsaved changes?') && this.setState({ edits: {}, editing: null })}>Discard</Button>}
					{canEdit && <Button primary icon='save' content='Save' title={editCount > 0 ? `Review and save ${editCount} change${editCount === 1 ? '' : 's'}` : 'No changes to save'} disabled={editCount === 0 || previewLoading} loading={previewLoading} onClick={this.openPreview} />}
				</div>

				{notConfigured &&
					<Message warning>
						<Message.Header>Services are not set up yet</Message.Header>
						{isAdmin ? 'Open Settings to choose the services (templates) and roles to show.' : 'Ask an admin to set up the Services page.'}
					</Message>
				}

				{!notConfigured &&
					<div style={{ overflowX: 'auto' }}>
						<Table celled structured unstackable compact className='svc-table' style={{ width: tableWidth }}>
							<Table.Header>
								<Table.Row>
									{this.renderHeaderCell('date', 'Date')}
									{this.renderHeaderCell('service', 'Service')}
									{config
										? roles.map(role => this.renderHeaderCell(role.id, role.name))
										: [0, 1, 2].map(i => <Table.HeaderCell key={i} style={{ width: DEFAULT_ROLE_WIDTH }}><SkeletonBar width='50%' /></Table.HeaderCell>)
									}
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{loading && this.renderSkeletonRows(config ? roles.length : 3)}
								{!loading && days.length === 0 &&
									<Table.Row><Table.Cell colSpan={2 + roles.length} textAlign='center' style={{ color: colors.midGray }}>No services this month</Table.Cell></Table.Row>
								}
								{!loading && days.map(({ day, rows: dayRows }) => dayRows.map((row, idx) => {
									const service = config.services.find(s => s.templateId === row.templateId);
									const title = serviceTitle(service, row);
									return (
										<Table.Row key={row.key} className={serviceTint(row.start)} style={{ opacity: row.past ? 0.75 : 1 }}>
											{idx === 0 &&
												<Table.Cell rowSpan={dayRows.length} verticalAlign='top' style={{ fontWeight: 'bold', background: colors.white }}>
													{format(new Date(`${day}T12:00:00`), 'EEE, MMM d')}
												</Table.Cell>
											}
											<Table.Cell verticalAlign='top' title={service?.label ?? row.label}>
												{row.eventId
													? <Link to={canEdit ? `/editor?id=${row.eventId}&tab=Schedule` : `/event?id=${row.eventId}`} style={{ fontWeight: 'bold' }}>{title}</Link>
													: <span style={{ fontWeight: 'bold' }}>{title}</span>
												}
											</Table.Cell>
											{roles.map(role => this.renderCell(row, role))}
										</Table.Row>
									);
								}))}
							</Table.Body>
						</Table>
					</div>
				}

				{!notConfigured && canEdit &&
					<ServicesHistoryPanel
						month={format(month, MONTH_PARAM)}
						monthLabel={monthLabel}
						activeServices={activeServices}
						activeRoles={activeRoles}
						reloadKey={this.state.historyKey}
					/>
				}

				{preview &&
					<ServicesPreviewPopup
						open={!!preview}
						preview={preview}
						memberNamesDict={memberNamesDict}
						saving={saving}
						notifyRoleNames={allRoles.filter(r => r.notify).map(r => r.name)}
						onClose={() => this.setState({ preview: null })}
						onConfirm={this.confirmSave}
					/>
				}

				{notify &&
					<ServicesNotifyPopup
						open={!!notify}
						results={notify.results}
						monthLabel={monthLabel}
						memberNamesDict={memberNamesDict}
						sending={notifySending}
						onClose={() => this.setState({ notify: null })}
						onConfirm={this.confirmNotify}
					/>
				}

				{showSettings &&
					<ServicesSettingsPopup
						open={showSettings}
						config={(setup?.config?.version ?? -1) > (config?.version ?? -1) ? setup.config : config}
						templates={setup?.templates}
						onClose={() => this.setState({ showSettings: false })}
						onSaved={(saved) => {
							servicesCache.Set(saved);
							this.setState({ showSettings: false, edits: {}, success: 'Services settings saved.' }, () => {
								this.load({ keepMessages: true });
								this.prefetchSetup();
							});
						}}
					/>
				}
			</Page>
		);
	}
}

export default withRouter(ServicesPage);
